import React, { useState, useEffect, useRef } from 'react';
import usePushNotifications from './hooks/usePushNotifications';
import './DirectMessages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function DirectMessages({ user, token, onClose, initialTab = 'conversations' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Push notifications
  const { isSupported: pushSupported, isSubscribed: pushEnabled, subscribe: subscribePush, unsubscribe: unsubscribePush, loading: pushLoading } = usePushNotifications(token);
  
  // Chat state
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatType, setChatType] = useState(null); // 'dm' or 'group'
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  // New request state
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // New group state
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch data on mount and set up polling
  useEffect(() => {
    fetchAllData();
    
    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      if (selectedChat) {
        if (chatType === 'dm') {
          fetchMessages(selectedChat.user_id);
        } else if (chatType === 'group') {
          fetchGroupMessages(selectedChat.id);
        }
      }
      fetchNotifications();
    }, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedChat?.user_id, selectedChat?.id, chatType]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchConversations(),
      fetchGroups(),
      fetchRequests(),
      fetchNotifications()
    ]);
    setLoading(false);
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dm/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dm/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dm/requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/dm/messages/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const fetchGroupMessages = async (groupId) => {
    try {
      const response = await fetch(`${API_URL}/api/dm/groups/${groupId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching group messages:', err);
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  const sendChatRequest = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${API_URL}/api/dm/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_user_id: selectedUser.id,
          message: requestMessage || null
        })
      });
      
      if (response.ok) {
        setShowNewRequest(false);
        setSelectedUser(null);
        setRequestMessage('');
        setSearchQuery('');
        setSearchResults([]);
        fetchRequests();
        alert('Chat request sent!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to send request');
      }
    } catch (err) {
      console.error('Error sending request:', err);
      alert('Failed to send request');
    }
  };

  const respondToRequest = async (requestId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/dm/request/${requestId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchRequests();
        if (action === 'accept') {
          fetchConversations();
        }
      } else {
        const data = await response.json();
        alert(data.detail || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error(`Error ${action}ing request:`, err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    try {
      const url = chatType === 'dm' 
        ? `${API_URL}/api/dm/messages/${selectedChat.user_id}`
        : `${API_URL}/api/dm/groups/${selectedChat.id}/messages`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return;
    
    try {
      const response = await fetch(`${API_URL}/api/dm/groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: groupName,
          member_ids: selectedMembers.map(m => m.user_id)
        })
      });
      
      if (response.ok) {
        setShowNewGroup(false);
        setGroupName('');
        setSelectedMembers([]);
        fetchGroups();
        alert('Group created!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to create group');
      }
    } catch (err) {
      console.error('Error creating group:', err);
    }
  };

  const markNotificationsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  const openConversation = async (conv) => {
    setSelectedChat(conv);
    setChatType('dm');
    setMessagesLoading(true);
    await fetchMessages(conv.user_id);
    setMessagesLoading(false);
  };

  const openGroup = async (group) => {
    setSelectedChat(group);
    setChatType('group');
    setMessagesLoading(true);
    await fetchGroupMessages(group.id);
    setMessagesLoading(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getRoleColor = (role) => {
    const colors = {
      'Founder': '#FFD700',
      'Elder': '#d4b978',
      'Aurel': '#d4b978',
      'Noble': '#c9a962',
      'Archivist': '#c9a962',
      'Knight': '#a68b4b',
      'Steward': '#a68b4b',
      'Squire': '#8b7355',
      'Initiate': '#6b5344',
      'Member': '#555555'
    };
    return colors[role] || '#555555';
  };

  return (
    <div className="dm-overlay" onClick={onClose}>
      <div className="dm-container" onClick={e => e.stopPropagation()}>
        <div className="dm-header">
          <h2>Messages</h2>
          <div className="dm-header-actions">
            {pushSupported && (
              <button 
                className={`push-toggle-btn ${pushEnabled ? 'enabled' : ''}`}
                onClick={pushEnabled ? unsubscribePush : subscribePush}
                disabled={pushLoading}
                title={pushEnabled ? 'Disable browser notifications' : 'Enable browser notifications'}
              >
                {pushLoading ? '...' : pushEnabled ? '🔔' : '🔕'}
              </button>
            )}
            {unreadCount > 0 && (
              <button className="mark-read-btn" onClick={markNotificationsRead}>
                Mark all read ({unreadCount})
              </button>
            )}
            <button className="dm-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="dm-tabs">
          <button 
            className={`dm-tab ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            Chats
            {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
              <span className="tab-badge">{conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}</span>
            )}
          </button>
          <button 
            className={`dm-tab ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            Groups
          </button>
          <button 
            className={`dm-tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Requests
            {requests.incoming?.length > 0 && (
              <span className="tab-badge">{requests.incoming.length}</span>
            )}
          </button>
          <button 
            className={`dm-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Alerts
            {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
          </button>
        </div>

        <div className="dm-content">
          {/* Chat View */}
          {selectedChat ? (
            <div className="dm-chat-view">
              <div className="chat-header">
                <button className="back-btn" onClick={() => setSelectedChat(null)}>←</button>
                <div className="chat-info">
                  <span className="chat-name">
                    {chatType === 'dm' ? selectedChat.username : selectedChat.name}
                  </span>
                  {chatType === 'dm' && (
                    <span className="chat-role" style={{ color: getRoleColor(selectedChat.role) }}>
                      {selectedChat.role}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="messages-container">
                {messagesLoading ? (
                  <div className="messages-loading">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="messages-empty">No messages yet. Say hello!</div>
                ) : (
                  messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`message ${msg.sender_id === user?.id ? 'own' : ''}`}
                    >
                      {msg.sender_id !== user?.id && (
                        <div className="message-sender" style={{ color: getRoleColor(msg.sender_role) }}>
                          {msg.sender_username}
                        </div>
                      )}
                      <div className="message-content">{msg.content}</div>
                      <div className="message-time">{formatTime(msg.created_at)}</div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <div className="message-input-area">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="message-input"
                />
                <button className="send-btn" onClick={sendMessage}>Send</button>
              </div>
            </div>
          ) : (
            <>
              {/* Conversations Tab */}
              {activeTab === 'conversations' && (
                <div className="conversations-list">
                  <button className="new-chat-btn" onClick={() => setShowNewRequest(true)}>
                    + New Chat Request
                  </button>
                  
                  {loading ? (
                    <div className="list-loading">Loading...</div>
                  ) : conversations.length === 0 ? (
                    <div className="list-empty">
                      <p>No conversations yet</p>
                      <p className="empty-hint">Send a chat request to start messaging</p>
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <div 
                        key={conv.user_id} 
                        className="conversation-item"
                        onClick={() => openConversation(conv)}
                      >
                        <div className="conv-avatar">
                          {conv.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="conv-info">
                          <div className="conv-header">
                            <span className="conv-name">{conv.username}</span>
                            <span className="conv-role" style={{ color: getRoleColor(conv.role) }}>
                              {conv.role}
                            </span>
                          </div>
                          {conv.last_message && (
                            <div className="conv-preview">{conv.last_message}</div>
                          )}
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="unread-badge">{conv.unread_count}</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                <div className="groups-list">
                  <button 
                    className="new-chat-btn" 
                    onClick={() => setShowNewGroup(true)}
                    disabled={conversations.length === 0}
                    title={conversations.length === 0 ? "You need at least one accepted contact" : ""}
                  >
                    + Create Group
                  </button>
                  
                  {groups.length === 0 ? (
                    <div className="list-empty">
                      <p>No group chats yet</p>
                      <p className="empty-hint">Create a group with your contacts</p>
                    </div>
                  ) : (
                    groups.map(group => (
                      <div 
                        key={group.id} 
                        className="conversation-item"
                        onClick={() => openGroup(group)}
                      >
                        <div className="conv-avatar group">
                          {group.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="conv-info">
                          <div className="conv-header">
                            <span className="conv-name">{group.name}</span>
                            <span className="group-members">{group.members?.length || 0} members</span>
                          </div>
                          {group.last_message && (
                            <div className="conv-preview">{group.last_message}</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Requests Tab */}
              {activeTab === 'requests' && (
                <div className="requests-list">
                  <h4>Incoming Requests</h4>
                  {requests.incoming?.length === 0 ? (
                    <div className="list-empty small">No pending requests</div>
                  ) : (
                    requests.incoming?.map(req => (
                      <div key={req.id} className="request-item">
                        <div className="request-info">
                          <div className="request-header">
                            <span className="request-name">{req.from_username}</span>
                            <span className="request-role" style={{ color: getRoleColor(req.from_role) }}>
                              {req.from_role}
                            </span>
                          </div>
                          {req.message && (
                            <div className="request-message">&ldquo;{req.message}&rdquo;</div>
                          )}
                        </div>
                        <div className="request-actions">
                          <button 
                            className="accept-btn"
                            onClick={() => respondToRequest(req.id, 'accept')}
                          >
                            Accept
                          </button>
                          <button 
                            className="reject-btn"
                            onClick={() => respondToRequest(req.id, 'reject')}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  <h4>Sent Requests</h4>
                  {requests.outgoing?.length === 0 ? (
                    <div className="list-empty small">No pending sent requests</div>
                  ) : (
                    requests.outgoing?.map(req => (
                      <div key={req.id} className="request-item outgoing">
                        <div className="request-info">
                          <span className="request-name">To: {req.to_username}</span>
                          <span className="request-status">Pending</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="notifications-list">
                  {notifications.length === 0 ? (
                    <div className="list-empty">No notifications</div>
                  ) : (
                    notifications.map(notif => (
                      <div 
                        key={notif.id} 
                        className={`notification-item ${notif.read ? '' : 'unread'}`}
                      >
                        <div className="notif-content">
                          <div className="notif-title">{notif.title}</div>
                          <div className="notif-message">{notif.message}</div>
                          <div className="notif-time">{formatTime(notif.created_at)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* New Chat Request Modal */}
        {showNewRequest && (
          <div className="modal-overlay" onClick={() => setShowNewRequest(false)}>
            <div className="new-request-modal" onClick={e => e.stopPropagation()}>
              <h3>Send Chat Request</h3>
              <input
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
                placeholder="Search users by username..."
                className="search-input"
              />
              
              {searching && <div className="searching">Searching...</div>}
              
              <div className="search-results">
                {searchResults.map(u => (
                  <div 
                    key={u.id}
                    className={`search-result ${selectedUser?.id === u.id ? 'selected' : ''} ${u.is_connected ? 'connected' : ''} ${u.pending_request ? 'pending' : ''}`}
                    onClick={() => !u.is_connected && !u.pending_request && setSelectedUser(u)}
                  >
                    <span className="result-name">{u.username}</span>
                    <span className="result-role" style={{ color: getRoleColor(u.role) }}>{u.role}</span>
                    {u.is_connected && <span className="result-status">Already connected</span>}
                    {u.pending_request && <span className="result-status">Request pending</span>}
                  </div>
                ))}
              </div>
              
              {selectedUser && (
                <div className="request-form">
                  <p>Send request to: <strong>{selectedUser.username}</strong></p>
                  <textarea
                    value={requestMessage}
                    onChange={e => setRequestMessage(e.target.value)}
                    placeholder="Add a message (optional)..."
                    className="request-message-input"
                  />
                  <div className="modal-actions">
                    <button className="cancel-btn" onClick={() => setShowNewRequest(false)}>Cancel</button>
                    <button className="send-request-btn" onClick={sendChatRequest}>Send Request</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* New Group Modal */}
        {showNewGroup && (
          <div className="modal-overlay" onClick={() => setShowNewGroup(false)}>
            <div className="new-group-modal" onClick={e => e.stopPropagation()}>
              <h3>Create Group Chat</h3>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="Group name..."
                className="group-name-input"
              />
              
              <h4>Select Members (from your contacts)</h4>
              <div className="members-select">
                {conversations.map(conv => (
                  <label key={conv.user_id} className="member-option">
                    <input
                      type="checkbox"
                      checked={selectedMembers.some(m => m.user_id === conv.user_id)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedMembers([...selectedMembers, conv]);
                        } else {
                          setSelectedMembers(selectedMembers.filter(m => m.user_id !== conv.user_id));
                        }
                      }}
                    />
                    <span className="member-name">{conv.username}</span>
                    <span className="member-role" style={{ color: getRoleColor(conv.role) }}>{conv.role}</span>
                  </label>
                ))}
              </div>
              
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowNewGroup(false)}>Cancel</button>
                <button 
                  className="create-group-btn" 
                  onClick={createGroup}
                  disabled={!groupName.trim() || selectedMembers.length === 0}
                >
                  Create Group ({selectedMembers.length + 1} members)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DirectMessages;
